import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { UserRole, type User } from '@hms/shared';
import { organizationsApi, type OrganizationDto } from '@/services/api/organizationsApi';

/**
 * Active-organization context store.
 *
 * Currency lives on the organization (one org = one country = one currency,
 * per migration 20260519000001), so this store owns the source of truth for
 * `currencyCode` / `currencySymbol`. The `useOrganizationCurrency` composable
 * reads from here.
 *
 * Hydration: `initFromUser` is called by the auth store after login and
 * session rehydration. It kicks off a background fetch of `GET /organizations/me`
 * which returns the caller's full org. Super-admins (who have no own org)
 * get a 404 from the endpoint — the store stays empty for them, and currency
 * formatting falls back to a plain number with no symbol.
 *
 * Persisted to localStorage so the UI has correct currency on the very first
 * paint after a reload, before the `/organizations/me` fetch resolves.
 */
export const useOrganizationContextStore = defineStore(
  'organizationContext',
  () => {
    const organization = ref<OrganizationDto | null>(null);
    const isLoading = ref(false);

    const currencyCode = computed<string | null>(() => organization.value?.currencyCode ?? null);
    const currencySymbol = computed<string | null>(() => organization.value?.currencySymbol ?? null);
    const country = computed<string | null>(() => organization.value?.country ?? null);

    const setOrganization = (org: OrganizationDto | null) => {
      organization.value = org;
    };

    const loadMine = async (): Promise<void> => {
      isLoading.value = true;
      try {
        organization.value = await organizationsApi.getMine();
      } catch (err) {
        // A failed refresh keeps the organization already known (persisted, or loaded earlier), so amounts keep
        // their currency; logout is what clears it.
        console.warn('Failed to load /organizations/me', err);
      } finally {
        isLoading.value = false;
      }
    };

    /**
     * Bootstrap from the authenticated user. Called by the auth store after a
     * successful login. Skips the fetch only for super-admins (they have no own
     * org). CORA-281: patients DO hydrate their org — they see currency on their
     * own invoices, and `GET /organizations/me` resolves the org from the JWT
     * for any authenticated role (including patients).
     */
    const initFromUser = (user: User | null): void => {
      if (!user) {
        reset();
        return;
      }

      if (user.role === UserRole.SUPER_ADMIN) {
        // Super-admins span all orgs; there's no "own" org. The currency
        // formatter just renders a plain number for them.
        organization.value = null;
        return;
      }

      // Fire and forget — the persist plugin already restored the previous
      // value, so the UI renders correct currency immediately on first paint.
      void loadMine();
    };

    const reset = () => {
      organization.value = null;
    };

    return {
      organization,
      isLoading,
      currencyCode,
      currencySymbol,
      country,
      setOrganization,
      loadMine,
      initFromUser,
      reset,
    };
  },
  {
    persist: {
      paths: ['organization'],
    },
  },
);
